import { PERSON_ROLE_LABELS, PERSON_ROLES, type PersonRole } from '@netcon-work/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { Benutzer } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * **Zugang über NetCon.Cloud** (05.11.2026, [E-462](../../../docs/ENTSCHEIDUNGEN.md#e-462)).
 *
 * Seit Work nur noch über NetCon.Cloud anmeldet (E-461), vergibt die Verwaltung keine Passwörter
 * mehr. Sie wählt Personen aus NetCon.Cloud oder lädt sie dort ein (ADR 0007 in NetCon.Cloud);
 * die Rechte in Work bleiben wie bisher hier – Rolle, Welten, WIP-Limit.
 */

type Ergebnis = { displayName: string; status: 'vorhanden' | 'angelegt'; einladung: string | null }

/** Was nach dem Freischalten passiert, in einem Satz für die Meldung oben. */
export function zugangSatz(r: Ergebnis): string {
  if (r.status === 'vorhanden') return `${r.displayName} hat jetzt Zugang zu Work.`
  if (r.einladung === 'verschickt')
    return `${r.displayName} ist eingeladen – NetCon.Cloud hat eine Mail geschickt, über die das Passwort gesetzt wird.`
  if (r.einladung === 'fehlgeschlagen')
    return `${r.displayName} ist angelegt, aber die Einladungsmail ging nicht raus. Bitte in NetCon.Cloud nachholen.`
  return `${r.displayName} hat jetzt Zugang – Anmeldung mit dem NetCon-Passwort.`
}

const RAHMEN =
  'fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[12vh]'
const KARTE =
  'w-full min-w-0 max-w-[520px] rounded-card border border-line bg-surface p-[18px] shadow-card'
const FELD =
  'mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal'

function Fehler({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <p className="mt-2.5 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]">
      {text}
    </p>
  )
}

/** Für eine Person, die Work schon kennt (aus GitLab, NetCon oder von Hand), aber ohne Zugang. */
export function ZugangGeben({
  person,
  schliessen,
  fertig,
}: {
  person: Benutzer
  schliessen: () => void
  fertig: (text: string) => void
}) {
  const [email, setEmail] = useState(person.email ?? '')
  const [fehler, setFehler] = useState<string | null>(null)
  const geben = useMutation({
    mutationFn: () => trpc.people.cloudZugang.mutate({ personId: person.id, email: email.trim() }),
    onSuccess: (r) => fertig(zugangSatz(r)),
    onError: (e: Error) => setFehler(e.message),
  })

  return (
    <div
      className={RAHMEN}
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label={`Zugang für ${person.displayName}`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          setFehler(null)
          geben.mutate()
        }}
        className={KARTE}
      >
        <h3 className="text-sm font-bold">Zugang für {person.displayName}</h3>
        <p className="mt-1 text-[11.5px] text-muted">
          Work meldet über NetCon.Cloud an. Gibt es dort ein Konto mit dieser Adresse, wird es für
          Work freigeschaltet; sonst lädt NetCon.Cloud ein – mit einer Firmenadresse ohne Mail
          (Anmeldung mit dem NetCon-Passwort), sonst mit einer Mail zum Passwort setzen.
        </p>
        <label className="mt-3 block text-[11.5px] font-semibold text-ink2">
          E-Mail
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            className={FELD}
          />
        </label>
        <Fehler text={fehler} />
        <div className="mt-3.5 flex gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={geben.isPending}>
            {geben.isPending ? 'Schaltet frei …' : 'Zugang geben'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={schliessen}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  )
}

/** Wartet mit der Suche, bis eine Weile nicht getippt wurde. */
function useVerzoegert(wert: string, ms = 300): string {
  const [verzoegert, setVerzoegert] = useState(wert)
  useEffect(() => {
    const t = window.setTimeout(() => setVerzoegert(wert), ms)
    return () => window.clearTimeout(t)
  }, [wert, ms])
  return verzoegert
}

/** Neue Person: aus NetCon.Cloud wählen – oder einladen, wenn sie dort noch fehlt. */
export function AusCloudHinzufuegen({
  schliessen,
  fertig,
}: {
  schliessen: () => void
  fertig: (text: string) => void
}) {
  const [suche, setSuche] = useState('')
  const [role, setRole] = useState<PersonRole>('mit_owner')
  const [einladen, setEinladen] = useState(false)
  const [email, setEmail] = useState('')
  const [vorname, setVorname] = useState('')
  const [nachname, setNachname] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const frage = useVerzoegert(suche.trim())

  const treffer = useQuery({
    queryKey: ['people', 'cloudSuchen', frage],
    queryFn: () => trpc.people.cloudSuchen.query({ suche: frage }),
    enabled: frage.length >= 3,
    staleTime: 30_000,
    retry: false,
  })

  const geben = useMutation({
    mutationFn: (v: {
      email: string
      personId?: string | undefined
      vorname?: string | undefined
      nachname?: string | undefined
    }) => trpc.people.cloudZugang.mutate({ ...v, role }),
    onSuccess: (r) => fertig(zugangSatz(r)),
    onError: (e: Error) => setFehler(e.message),
  })

  return (
    <div
      className={RAHMEN}
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Person aus NetCon.Cloud hinzufügen"
    >
      <div className={KARTE}>
        <h3 className="text-sm font-bold">Person hinzufügen</h3>
        <p className="mt-1 text-[11.5px] text-muted">
          Aus den Konten in NetCon.Cloud wählen. Wer dort noch fehlt, wird eingeladen.
        </p>

        <label className="mt-3 block text-[11.5px] font-semibold text-ink2">
          Rolle in Work
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as PersonRole)}
            className={`${FELD} cursor-pointer`}
          >
            {PERSON_ROLES.map((r) => (
              <option key={r} value={r}>
                {PERSON_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <p className="mt-1 text-[11px] text-muted">
          Gilt nur für neue Personen; wen Work schon kennt, behält seine Rolle.
        </p>

        {!einladen ? (
          <>
            <label className="mt-3 block text-[11.5px] font-semibold text-ink2">
              Name oder Adresse
              <input
                autoFocus
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="mindestens drei Zeichen"
                autoComplete="off"
                className={FELD}
              />
            </label>

            <div className="mt-2 max-h-[38vh] overflow-y-auto">
              {treffer.isFetching && <p className="py-2 text-[12px] text-muted">Sucht …</p>}
              {treffer.error && <Fehler text={treffer.error.message} />}
              {treffer.data?.length === 0 && (
                <p className="py-2 text-[12px] text-muted">Kein Konto gefunden.</p>
              )}
              <ul>
                {treffer.data?.map((k) => (
                  <li
                    key={k.sub}
                    className="flex items-center gap-2 border-b border-line py-2 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold">
                        {k.name ?? k.email}
                        {k.extern && (
                          <span className="ml-1.5 rounded bg-[#f3f2ef] px-1 text-[10px] font-semibold text-muted">
                            extern
                          </span>
                        )}
                        {!k.aktiv && (
                          <span className="ml-1.5 rounded bg-[#fdeaea] px-1 text-[10px] font-semibold text-[#b02a2a]">
                            gesperrt
                          </span>
                        )}
                      </div>
                      <div className="truncate text-[11.5px] text-muted">
                        {k.email}
                        {k.personName && ` · in Work: ${k.personName}`}
                        {k.freigeschaltet && ' · für Work freigeschaltet'}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={geben.isPending || !k.aktiv || !k.email}
                      onClick={() => {
                        setFehler(null)
                        if (k.email)
                          geben.mutate({ email: k.email, personId: k.personId ?? undefined })
                      }}
                    >
                      {k.personId ? 'Zugang geben' : 'Übernehmen'}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => {
                setFehler(null)
                setEinladen(true)
                if (suche.includes('@')) setEmail(suche.trim())
              }}
              className="mt-2 cursor-pointer text-[12px] font-semibold text-accent hover:underline"
            >
              Nicht dabei? Einladen …
            </button>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              setFehler(null)
              geben.mutate({
                email: email.trim(),
                vorname: vorname.trim() || undefined,
                nachname: nachname.trim() || undefined,
              })
            }}
          >
            <label className="mt-3 block text-[11.5px] font-semibold text-ink2">
              E-Mail
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
                className={FELD}
              />
            </label>
            <div className="mt-2.5 flex gap-2">
              <label className="block flex-1 text-[11.5px] font-semibold text-ink2">
                Vorname
                <input
                  value={vorname}
                  onChange={(e) => setVorname(e.target.value)}
                  className={FELD}
                />
              </label>
              <label className="block flex-1 text-[11.5px] font-semibold text-ink2">
                Nachname
                <input
                  value={nachname}
                  onChange={(e) => setNachname(e.target.value)}
                  className={FELD}
                />
              </label>
            </div>
            <p className="mt-1.5 text-[11px] text-muted">
              Firmenadressen melden sich mit dem NetCon-Passwort an, ohne Mail. Andere bekommen von
              NetCon.Cloud eine Mail, über die sie ihr Passwort setzen.
            </p>
            <div className="mt-3 flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={geben.isPending}>
                {geben.isPending ? 'Lädt ein …' : 'Einladen'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setEinladen(false)}>
                Zurück zur Suche
              </Button>
            </div>
          </form>
        )}

        <Fehler text={fehler} />
        <div className="mt-3.5 flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={schliessen}>
            Schließen
          </Button>
        </div>
      </div>
    </div>
  )
}
